/**
 * Where a person who came from the marketing site is headed (plan 0028 slice 1): the site links
 * to /registrer?next=demo or ?next=oppsett, and the choice has to survive the confirmation mail,
 * which opens in a new tab. So it is kept in localStorage, not sessionStorage, and read after
 * login; it is forgotten where the journey arrives. Only the two names are accepted: a query parameter never carries a price, a plan or a
 * path (an open redirect).
 */
const KEY = 'timefly.next';
/** the trade route (`demo=bygg`, #128): a company made at the end of it starts with planning off (#118) */
const SEED = 'timefly.seed';
const PATHS = { demo: '/konto/demo', oppsett: '/konto/opprett-bedrift' } as const;
export type Destination = keyof typeof PATHS;

const isDestination = (v: unknown): v is Destination => typeof v === 'string' && Object.hasOwn(PATHS, v);

/** Keeps `?next=` when it names a destination; anything else leaves what was kept alone. */
export function rememberDestination(search: string): void {
  const q = new URLSearchParams(search);
  const next = q.get('next');
  try {
    // only the one value is kept; a later visit without it leaves it alone, as `next` is
    if (q.get('demo') === 'bygg') localStorage.setItem(SEED, 'bygg');
    if (isDestination(next)) localStorage.setItem(KEY, next);
  } catch {
    // private window or blocked storage: the person lands on the default page instead
  }
}

/** Whether this person came through the trade route, so the company they make starts without planning. */
export function cameThroughTrade(): boolean {
  try {
    return localStorage.getItem(SEED) === 'bygg';
  } catch {
    return false;
  }
}

/** The kept destination's page, or the company setup page when none is kept. Reading keeps it:
 * a guard may render twice, and the person may reload on the way. */
export function destinationPath(): string {
  let next: string | null = null;
  try {
    next = localStorage.getItem(KEY);
  } catch {
    // as above
  }
  return isDestination(next) ? PATHS[next] : PATHS.oppsett;
}

/** Called where the journey arrives (a company or a demo made), so a later login starts fresh. */
export function forgetDestination(): void {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(SEED);
  } catch {
    // as above
  }
}
